/**
 * 施工実績カテゴリ作成・編集ページ
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminClient, TypeUtils } from "../../../api/generated/APIClient";
import { getImageUrl } from "../../../utils/imageFetcher";
import "./ResultCategoryFormPage.css";

// File → base64 (without data URI prefix)
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ResultCategoryFormData {
  id?: string;
  name: string;
  description: string;
  displayOrder: number;
  thumbnailUrl: string;
  isEnabled: boolean;
  createDate?: string;
  updateDate?: string;
  version?: string;
}

export const ResultCategoryFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<ResultCategoryFormData>({
    name: "",
    description: "",
    displayOrder: 0,
    thumbnailUrl: "",
    isEnabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // カテゴリサムネイル画像のアップロード処理
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("画像ファイルを選択してください");
      return;
    }
    // 新規作成時はカテゴリ ID をまだ持たない → クライアント側で UUIDv7 風 ID を発行して formData に保持
    const categoryId = formData.id || crypto.randomUUID();
    setIsUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch("/api/admin/result-category-thumbnail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category_id: categoryId,
          file_name: file.name,
          content_type: file.type,
          file_data: base64,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `アップロードに失敗 (${res.status})`);
      }
      const data = await res.json();
      setFormData((prev) => ({
        ...prev,
        id: categoryId,
        thumbnailUrl: data.storage_path,
      }));
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "アップロードに失敗しました");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveThumbnail = () => {
    setFormData((prev) => ({ ...prev, thumbnailUrl: "" }));
  };

  // 編集モードの場合、データを取得
  useEffect(() => {
    if (isEditMode && id) {
      setIsLoading(true);
      const fetchCategory = async () => {
        try {
          const response = await (AdminClient as any).IndexResultCategory({
            offset: 0,
            limit: 0,
          });

          let items: any[] = [];
          if (response?.data) {
            if (Array.isArray(response.data)) {
              items = response.data;
            } else if (
              response.data.resultCategorys &&
              Array.isArray(response.data.resultCategorys)
            ) {
              items = response.data.resultCategorys;
            } else if (
              response.data.result_categorys &&
              Array.isArray(response.data.result_categorys)
            ) {
              items = response.data.result_categorys;
            } else if (
              response.data.items &&
              Array.isArray(response.data.items)
            ) {
              items = response.data.items;
            }
          }

          const category = items.find((item: any) => item.id === id);
          if (category) {
            setFormData({
              id: category.id,
              name: category.name || "",
              description: category.description || "",
              displayOrder: category.displayOrder ?? category.display_order ?? 0,
              thumbnailUrl: category.thumbnailUrl || category.thumbnail_url || "",
              isEnabled: category.isEnabled ?? category.is_enabled ?? true,
              createDate: category.createDate || category.create_date,
              updateDate: category.updateDate || category.update_date,
              version: category.version,
            });
          }
        } catch (error) {
          console.error("カテゴリの取得に失敗しました:", error);
          alert("カテゴリの取得に失敗しました");
        } finally {
          setIsLoading(false);
        }
      };

      fetchCategory();
    }
  }, [id, isEditMode]);

  const handleInputChange = (field: keyof ResultCategoryFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "カテゴリ名を入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const categoryId = formData.id || crypto.randomUUID();

      // 日付をDate型に変換するヘルパー
      const toDate = (date: any): Date => {
        if (!date) return new Date();
        if (date instanceof Date) return date;
        if (typeof date === "string") return new Date(date);
        if (date.seconds !== undefined) {
          const seconds =
            typeof date.seconds === "bigint"
              ? Number(date.seconds)
              : date.seconds;
          return new Date(seconds * 1000);
        }
        return new Date();
      };

      const submitData = {
        id: categoryId,
        name: formData.name,
        description: formData.description,
        displayOrder: formData.displayOrder,
        thumbnailUrl: formData.thumbnailUrl,
        isEnabled: formData.isEnabled,
        createDate: TypeUtils.dateToTimestamp(
          isEditMode ? toDate(formData.createDate) : new Date()
        ),
        updateDate: TypeUtils.dateToTimestamp(new Date()),
        version: isEditMode ? formData.version : "v1",
      };

      if (isEditMode) {
        await (AdminClient as any).UpdateResultCategory({
          resultCategorys: [submitData],
        });
        alert("カテゴリを更新しました");
        navigate(`/admin/entities/result-category/${id}`);
      } else {
        await (AdminClient as any).CreateResultCategory({
          resultCategorys: [submitData],
        });
        alert("カテゴリを作成しました");
        navigate("/admin/entities/result-category");
      }
    } catch (error) {
      console.error("保存に失敗しました:", error);
      alert(
        `保存に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/admin/entities/result-category/${id}`);
    } else {
      navigate("/admin/entities/result-category");
    }
  };

  if (isLoading) {
    return (
      <div className="result-category-form-page">
        <div className="loading">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="result-category-form-page">
      <div className="result-category-form-container">
        <div className="result-category-form-header">
          <h1>{isEditMode ? "カテゴリを編集" : "新しいカテゴリを作成"}</h1>
          <p className="form-description">
            {isEditMode
              ? "カテゴリの内容を編集できます"
              : "新しい施工実績カテゴリを登録します"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="result-category-form">
          {/* 基本情報セクション */}
          <section className="form-section">
            <h2 className="section-title">基本情報</h2>

            {/* カテゴリ名 */}
            <div className="form-field">
              <label htmlFor="name" className="form-label">
                カテゴリ名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`form-input ${errors.name ? "error" : ""}`}
                placeholder="例：物流倉庫、福祉施設、店舗など"
              />
              {errors.name && (
                <div className="error-message">{errors.name}</div>
              )}
            </div>

            {/* 説明 */}
            <div className="form-field">
              <label htmlFor="description" className="form-label">
                説明
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="カテゴリの説明を入力してください"
              />
            </div>

            {/* 表示順序 */}
            <div className="form-field">
              <label htmlFor="displayOrder" className="form-label">
                表示順序
              </label>
              <input
                type="number"
                id="displayOrder"
                value={formData.displayOrder}
                onChange={(e) =>
                  handleInputChange("displayOrder", Number(e.target.value))
                }
                className="form-input"
                min="0"
              />
              <p className="field-help">
                数値が小さいほど先に表示されます
              </p>
            </div>

            {/* 有効フラグ */}
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) =>
                    handleInputChange("isEnabled", e.target.checked)
                  }
                />
                <span>有効にする</span>
              </label>
              <p className="field-help">
                無効にすると、このカテゴリは選択肢に表示されなくなります
              </p>
            </div>
          </section>

          {/* 画像セクション */}
          <section className="form-section">
            <h2 className="section-title">画像</h2>

            {/* サムネイル画像（ファイルアップロード） */}
            <div className="form-field">
              <label className="form-label">サムネイル画像</label>
              {formData.thumbnailUrl ? (
                <div className="image-preview" style={{ position: "relative" }}>
                  <img
                    src={getImageUrl(formData.thumbnailUrl) || ""}
                    alt="サムネイルプレビュー"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      画像を差し替え
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleRemoveThumbnail}
                      disabled={isUploading}
                    >
                      画像を外す
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? "アップロード中..." : "画像を選択"}
                  </button>
                  <p className="field-help" style={{ marginTop: 4 }}>
                    JPG / PNG / WebP など。アップロード後にカテゴリ詳細・一覧で使われます。
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                style={{ display: "none" }}
              />
            </div>
          </section>

          {/* ボタン */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "保存中..."
                : isEditMode
                ? "更新する"
                : "作成する"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
